import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows, type KstDataGridPager, type KstDataGridHeaderButton } from '@/components/KstDataGrid';
import { ConfirmModal } from '@/components/ConfirmModal';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { hasValue } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';

import { uploadBlockBlob } from '@/services/azure-blob-service';
// Constructors and values: access lazily via (window as any).X at usage sites
import { getAvailableSubmodules } from '@/utils/auth';
import { isInArray } from '@/utils/arrays';

const mapOverlayKmlColumns: KstDataGridColumn[] = [
  { field: 'id', name: 'ID', size: 'hidden' },
  {
    field: 'title',
    name: 'Name / Download',
    size: 'col-xs-3',
    template: (e: any) => {
      return `<a href="${e.signedURL}" target="_blank">${e.title}</a>`;
    },
  },
  { field: 'filename', name: 'Filename', size: 'col-xs-3' },
  {
    field: 'dateCreated',
    name: 'Uploaded At',
    size: 'col-xs-3',
    template: (e: any) => {
      return moment(e.dateCreated).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT).toString();
    },
  },
  { field: 'createdBy', name: 'Uploaded By', size: 'col-xs-3' },
];

const headerButtons: KstDataGridHeaderButton[] = [
  { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

export function MapOverlayKmlScreen() {
  const { theme } = useTheme();

  const [entries, setEntries] = useState<any[] | null>(null);
  const [doesDataExist, setDoesDataExist] = useState(true);
  const [isUpdatePermissioned, setIsUpdatePermissioned] = useState(false);
  const [isKmlLoading, setIsKmlLoading] = useState<boolean>(false);

  // Add modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addDocumentName, setAddDocumentName] = useState('');
  const [addFilename, setAddFilename] = useState<string | null>(null);
  const [isAddButtonEnabled, setIsAddButtonEnabled] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete confirm modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmItems, setDeleteConfirmItems] = useState<any[]>([]);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isAddSaving, setIsAddSaving] = useState<boolean>(false);

  const rows: KstDataGridRows = useMemo(() => ({ entries }), [entries]);
  const pager: KstDataGridPager = useMemo(
    () => ({ curPage: null as any, numPages: null as any, records: null as any, isVisible: false }),
    []
  );

  const gridButtons = useMemo(
    () => (isUpdatePermissioned ? headerButtons : []),
    [isUpdatePermissioned]
  );

  const refreshList = useCallback(() => {
    setIsKmlLoading(true);
    api.getMapOverlayKmlList((result: any) => {
      const list = result.attachmentList || [];
      setEntries(list);
      setDoesDataExist(list.length > 0);
      setIsKmlLoading(false);
    }, () => {
      App.showPopupNotification('Error getting KML/KMZ. Please try again.', 'error');
      setIsKmlLoading(false);
    });
  }, []);

  // Check permissions and load on mount
  useEffect(() => {
    const submodules = getAvailableSubmodules();
    const hasPermission = hasValue(submodules) ? isInArray(submodules, AccessAuthorities.ROLE_UPDATE_MAP_OVERLAY) : false;
    setIsUpdatePermissioned(hasPermission);
    refreshList();
  }, [refreshList]);

  const handleButtonCommand = useCallback((functionName: string, checkedItems: any[]) => {
    if (functionName === 'add') {
      setAddDocumentName('');
      setAddFilename(null);
      setIsAddButtonEnabled(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setAddModalOpen(true);
    } else if (functionName === 'delete') {
      if (checkedItems.length === 0) {
        App.showPopupNotification('Please select a file to delete by clicking the checkbox.', 'warning');
        return;
      }
      const count = checkedItems.length;
      const message = count === 1
        ? 'Are you sure you want to delete this KML/KMZ file?'
        : `Are you sure you want to delete ${count} KML/KMZ files?`;
      setDeleteConfirmItems(checkedItems);
      setDeleteConfirmMessage(message);
      setDeleteConfirmOpen(true);
    }
  }, []);

  const handleAddAttachment = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    if (input.files && input.files.length > 0) {
      setAddFilename(input.files[0].name);
      setIsAddButtonEnabled(true);
    }
  }, []);

  const handleSaveMapOverlayKml = useCallback(() => {
    if (!fileInputRef.current || !fileInputRef.current.files || fileInputRef.current.files.length === 0) {
      App.showPopupNotification('Please select a file.', 'error');
      return;
    }

    const file = fileInputRef.current.files[0];
    const attachment = new (window as any).Attachment();
    attachment.title = addDocumentName || file.name;
    attachment.filename = file.name;

    setIsAddSaving(true);
    api.saveAttachment([attachment], (result: any) => {
      if (!result.azureStorageBlobInfoList?.length) {
        setIsAddSaving(false);
        App.showPopupNotification('Error preparing file upload.', 'error');
        return;
      }

      const blobInfo = result.azureStorageBlobInfoList[0];

      uploadBlockBlob(blobInfo.host, blobInfo.sasWriteToken, blobInfo.containerName, blobInfo.blobName, file).then(() => {
        const addMapOverlayKmlData = {
          attachment: {
            id: blobInfo.attachmentId,
            activationKey: blobInfo.activationKey,
          },
        };

        api.addMapOverlayKml(addMapOverlayKmlData, () => {
          setIsAddSaving(false);
          setAddModalOpen(false);
          refreshList();
          App.showPopupNotification('KML/KMZ uploaded successfully.', 'success');
        }, () => {
          setIsAddSaving(false);
          App.showPopupNotification('Error adding KML/KMZ.', 'error');
        });
      }).catch(() => {
        setIsAddSaving(false);
        App.showPopupNotification('Error uploading file.', 'error');
      });
    });
  }, [addDocumentName, refreshList]);

  const handleAddCancel = useCallback(() => {
    setAddModalOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    const checkedItems = deleteConfirmItems;
    let count = 0;
    const len = checkedItems.length;

    setIsDeleting(true);
    for (let i = 0; i < len; i++) {
      api.removeMapOverlayKml(checkedItems[i].id, () => {
        count++;
        if (count === len) {
          setIsDeleting(false);
          refreshList();
          setDeleteConfirmOpen(false);
          setDeleteConfirmItems([]);
          setDeleteConfirmMessage('');
        }
      }, () => {
        setIsDeleting(false);
        App.showPopupNotification('Error removing KML/KMZ.', 'error');
      });
    }
  }, [deleteConfirmItems, refreshList]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, []);

  return (
    <div className={`map-overlay-kml-screen ${theme}`}>
      {/* Add KML/KMZ Modal */}
      <ConfirmModal
        open={addModalOpen}
        title="Add KML/KMZ"
        confirmText="Add"
        confirmDisabled={!isAddButtonEnabled || isAddSaving}
        busy={isAddSaving}
        theme={theme}
        onConfirm={handleSaveMapOverlayKml}
        onCancel={handleAddCancel}
      >
        <div className="kst-form-group">
          {addFilename ? (
            <div className="kst-file-selected">
              <i className="fa fa-file"></i> {addFilename}
            </div>
          ) : (
            <div>
              <button type="button" className="btn btn-default" onClick={handleAddAttachment}>
                <i className="fa fa-plus-circle"></i> Select File
              </button>
            </div>
          )}
        </div>
        <div className="kst-form-group" style={{ marginTop: '15px' }}>
          <input
            type="text"
            className="form-control"
            value={addDocumentName}
            onChange={(e) => setAddDocumentName(e.target.value)}
            placeholder="Document Name (optional)"
          />
        </div>
      </ConfirmModal>

      {/* Delete Confirm Modal */}
      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete KML/KMZ"
        message={deleteConfirmMessage}
        confirmText="Delete"
        confirmClass="danger"
        confirmDisabled={isDeleting}
        busy={isDeleting}
        autoFocus="cancel"
        scrollLock={true}
        theme={theme}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">Manage KML/KMZ</h3>
                {isUpdatePermissioned && (
                  <p className="kst-page-description">
                    KML/KMZ files can be added to your maps.
                  </p>
                )}
                {!isUpdatePermissioned && !doesDataExist && (
                  <p className="kst-page-description">
                    KML/KMZ files will appear below when uploaded.
                  </p>
                )}
              </div>

              <KstDataGrid
                columns={mapOverlayKmlColumns}
                rows={rows}
                headerbuttons={gridButtons}
                pager={pager}
                loading={isKmlLoading}
                showcheckbox={isUpdatePermissioned}
                showheader={isUpdatePermissioned}
                mobiletitlefield="title"
                onButtonCommand={handleButtonCommand}
              />
            </div>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        id="MapOverlayKmlAttachmentsFileInput"
        style={{ display: 'none' }}
        accept=".kml,.kmz"
        onChange={handleFileChange}
      />
    </div>
  );
}

export default MapOverlayKmlScreen;
